import { Injectable } from '@angular/core';
import { ImmutableContext } from '@ngxs-labs/immer-adapter';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import _ from 'lodash';
import { forkJoin } from 'rxjs';
import { BrickInstancesWithTabSettingsRemoteDto } from 'src/generated/signalR/brick-instances-with-tab-settings-remote-dto';
import { BrickTabConfigRemoteDto, BrickTabSettingsRemoteDto, BrickWallService, UpdateBrickTabSettingsRequestData } from 'src/generated/swagger';
import { SetTabIndex } from '../brickTypes/tab-brick-behaviour/tab-brick-behaviour.actions';
import {
   BrickTabSettingsUpdatedByServer,
   PersistAllTabConfigurations,
   PersistTabConfiguration,
   SetAndPersistSelectedTabIndex,
   SetSelectedTabIndex,
} from './brick-tab.actions';

const DEFAULT_TAB_INDEX = 0;

export interface BrickTabStateModel {
   bricks: Instance;
   unsavedTabsOnBrickInstances: string[];
}

export interface Instance {
   [brickInstanceId: string]: Brick;
}

export interface Brick {
   [brickId: string]: BrickTabConfigRemoteDto;
}

@State<BrickTabStateModel>({
   name: 'brickTabs',
   defaults: {
      bricks: {},
      unsavedTabsOnBrickInstances: [],
   },
})
@Injectable()
export class BrickTabState {
   constructor(private brickWallService: BrickWallService, private store: Store) {}

   @Selector([BrickTabState])
   public static bricks(state: BrickTabStateModel) {
      return state.bricks;
   }

   @Selector([BrickTabState])
   public static unsavedTabsOnBrickInstances(state: BrickTabStateModel) {
      return state.unsavedTabsOnBrickInstances;
   }

   @Selector([BrickTabState.bricks])
   public static getIsBrickInstanceSet(bricks: Instance) {
      return (brickInstanceId: string) => {
         return !_.isNil(bricks[brickInstanceId]);
      };
   }

   @Selector([BrickTabState.getBrickInstance])
   public static getIsBrickConfigSet(getBrickInstance: (brickInstanceId: string) => Brick) {
      return (brickInstanceId: string, gridId: string) => {
         return !_.isNil(getBrickInstance(brickInstanceId)[gridId]);
      };
   }

   @Selector([BrickTabState.bricks, BrickTabState.getIsBrickInstanceSet])
   public static getBrickInstance(bricks: Instance, getIsBrickInstanceSet: (brickInstanceId: string) => boolean) {
      return (brickInstanceId: string) => {
         return getIsBrickInstanceSet(brickInstanceId) ? bricks[brickInstanceId] : {};
      };
   }

   @Selector([BrickTabState.getBrickInstance, BrickTabState.getIsBrickConfigSet])
   public static getBrickConfig(
      getBrickInstance: (brickInstanceId: string) => Brick,
      getIsBrickConfigSet: (brickInstanceId: string, gridId: string) => boolean
   ) {
      return (brickInstanceId: string, gridId: string) => {
         return getIsBrickConfigSet(brickInstanceId, gridId) ? getBrickInstance(brickInstanceId)[gridId] : { selectedIndex: DEFAULT_TAB_INDEX };
      };
   }

   @Selector([BrickTabState.getBrickConfig])
   public static getIndexPersisted(getBrick: (brickInstanceId: string, gridId: string) => BrickTabConfigRemoteDto) {
      return (brickInstanceId: string, gridId: string) => {
         return !_.isNil(getBrick(brickInstanceId, gridId)?.selectedIndex);
      };
   }

   @Selector([BrickTabState.getBrickConfig])
   public static getSelectedIndex(getBrick: (brickInstanceId: string, gridId: string) => BrickTabConfigRemoteDto) {
      return (brickInstanceId: string, gridId: string) => {
         return getBrick(brickInstanceId, gridId)?.selectedIndex ?? DEFAULT_TAB_INDEX;
      };
   }

   @Selector([BrickTabState.getIsBrickConfigSet, BrickTabState.getSelectedIndex])
   public static getIsIndexAlreadySet(
      getIsBrickConfigSet: (brickInstanceId: string, gridId: string) => boolean,
      getSelectedIndex: (brickInstanceId: string, gridId: string) => number
   ) {
      return (brickInstanceId: string, gridId: string, index) => {
         return getIsBrickConfigSet(brickInstanceId, gridId) && getSelectedIndex(brickInstanceId, gridId) === index;
      };
   }

   @Selector([BrickTabState.getSelectedIndex])
   private static getUpdateBrickTabSettingsRequestData(getSelectedIndex: (brickInstanceId: string, gridId: string) => number) {
      return (brickInstanceId: string, gridId: string) => {
         const index = getSelectedIndex(brickInstanceId, gridId);
         const tabConfig: BrickTabConfigRemoteDto = { selectedIndex: index };
         const tabSettings: BrickTabSettingsRemoteDto = { tabConfigs: { [gridId]: tabConfig } };
         const brickTabSettings: { [key: string]: BrickTabSettingsRemoteDto } = { [brickInstanceId]: tabSettings };
         const requestData: UpdateBrickTabSettingsRequestData = {
            brickInstanceId: brickInstanceId,
            brickTabSettings: brickTabSettings,
         };

         return requestData;
      };
   }

   private augmentState(state: Readonly<BrickTabStateModel>, brickInstanceId: string, gridName: string) {
      if (_.isNil(state.bricks[brickInstanceId])) {
         state.bricks[brickInstanceId] = {};
      }
      if (_.isNil(state.bricks[brickInstanceId][gridName])) {
         state.bricks[brickInstanceId][gridName] = { selectedIndex: DEFAULT_TAB_INDEX };
      }
   }

   private getFiltered(data: { [key: string]: BrickInstancesWithTabSettingsRemoteDto }) {
      const result = _(data)
         .mapValues((setting) => setting?.brickTabSettings)
         .filter((setting) => !_.isNil(setting) && !_.isEmpty(setting))
         .map((dataItem) => {
            const dynamicKey = _.keys(dataItem)[0];

            return {
               brickInstanceId: dynamicKey,
               config: _.get(dataItem, [dynamicKey, 'tabConfigs']),
            };
         })
         .value();

      return result;
   }

   private getTabConfigData(
      data: {
         brickInstanceId: string;
         config: {
            [key: string]: BrickTabConfigRemoteDto;
         };
      }[]
   ) {
      return _(data)
         .map((dataItem) => {
            const dynamicKey = _.keys(dataItem.config)[0];
            const defaultConfig = _.get(dataItem.config, dynamicKey);

            return {
               brickInstanceId: dataItem.brickInstanceId,
               brickId: dynamicKey,
               selectedIndex: defaultConfig?.selectedIndex,
            };
         })
         .filter(({ selectedIndex }) => !_.isNil(selectedIndex))
         .value();
   }

   @Action(BrickTabSettingsUpdatedByServer)
   @ImmutableContext()
   public brickTabSettingsUpdatedByServer(ctx: StateContext<BrickTabStateModel>, action: BrickTabSettingsUpdatedByServer) {
      const values = this.getTabConfigData(this.getFiltered(action.data));

      values.forEach((value) => ctx.dispatch(new SetSelectedTabIndex(value.brickInstanceId, value.selectedIndex, value.brickId)));
      values.forEach((value) => ctx.dispatch(new SetTabIndex(value.brickInstanceId, value.selectedIndex)));
   }

   @Action(SetSelectedTabIndex)
   @ImmutableContext()
   public setSelectedTabIndex(ctx: StateContext<BrickTabStateModel>, action: SetSelectedTabIndex) {
      ctx.setState((state) => {
         this.augmentState(state, action.brickInstanceId, action.gridId);
         state.bricks[action.brickInstanceId][action.gridId].selectedIndex = action.selectedTabIndex;
         return state;
      });
   }

   @Action(PersistTabConfiguration)
   @ImmutableContext()
   public persistTabConfiguration(ctx: StateContext<BrickTabStateModel>, action: PersistTabConfiguration) {
      return this.brickWallService.brickTabSettings(
         this.store.selectSnapshot(BrickTabState.getUpdateBrickTabSettingsRequestData)(action.brickInstanceId, action.gridId)
      );
   }

   @Action(PersistAllTabConfigurations)
   @ImmutableContext()
   public persistAllTabConfiguration(ctx: StateContext<BrickTabStateModel>, action: PersistAllTabConfigurations) {
      const observables = action.brickInstanceIds.map((brickInstanceId) => ctx.dispatch(new PersistTabConfiguration(brickInstanceId)));

      if (_.isEmpty(observables)) {
         return;
      }
      return forkJoin(observables);
   }

   @Action(SetAndPersistSelectedTabIndex)
   @ImmutableContext()
   public setAndPersistSelectedTabIndex(ctx: StateContext<BrickTabStateModel>, action: SetAndPersistSelectedTabIndex) {
      if (this.store.selectSnapshot(BrickTabState.getIsIndexAlreadySet)(action.brickInstanceId, action.gridId, action.selectedTabIndex)) {
         return;
      }
      ctx.dispatch(new SetSelectedTabIndex(action.brickInstanceId, action.selectedTabIndex));
      ctx.dispatch(new PersistTabConfiguration(action.brickInstanceId));
   }
}
